/**
 * Video Download Notification
 * Small dismissable notification in top-right corner
 * Shows progress of video generation and auto-downloads when ready
 */

import React, { useState, useEffect } from 'react';
import videoDownloadManager from '../utils/videoDownloadManager';
import { showToast } from './Toast';
import logger from '../utils/logger';

const VideoDownloadNotification = () => {
  const [activeDownloads, setActiveDownloads] = useState([]);
  const [dismissedTasks, setDismissedTasks] = useState(new Set());
  const [downloadedTasks, setDownloadedTasks] = useState(new Set()); // Track which tasks have already triggered download

  useEffect(() => {
    // Subscribe to download updates
    const unsubscribe = videoDownloadManager.subscribe((downloads) => {
      setActiveDownloads(downloads);

      // Check for completed downloads and trigger download
      downloads.forEach(download => {
        if (download.status === 'completed' && download.videoUrl && !downloadedTasks.has(download.taskId)) {
          // Auto-download the video (only once per taskId)
          triggerVideoDownload(download.videoUrl, download.title);
          // Mark as downloaded so we don't trigger again
          setDownloadedTasks(prev => new Set([...prev, download.taskId]));
          // Don't show toast - the notification itself shows completion
        } else if (download.status === 'failed' && !dismissedTasks.has(download.taskId)) {
          showToast(download.error || 'Video generation failed', 'error');
        }
      });
    });

    return unsubscribe;
  }, [dismissedTasks, downloadedTasks]);

  const triggerVideoDownload = async (videoUrl, title) => {
    logger.log(`📥 Downloading video from: ${videoUrl}`);
    try {
      const response = await fetch(videoUrl, { mode: 'cors' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${title || 'storybook'}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      logger.log('✅ Download triggered successfully');
    } catch (err) {
      logger.error('Failed to download video:', err);
      // Fallback: open in new tab
      window.open(videoUrl, '_blank');
    }
  };

  const handleDismiss = (taskId) => {
    setDismissedTasks(prev => new Set([...prev, taskId]));
  };

  const handleCancel = (taskId) => {
    videoDownloadManager.cancelDownload(taskId);
    showToast('Video generation cancelled', 'info');
  };

  // Filter out dismissed downloads
  const visibleDownloads = activeDownloads.filter(
    download => !dismissedTasks.has(download.taskId) && download.status !== 'completed'
  );

  if (visibleDownloads.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-6 right-6 z-40 max-w-sm">
      {visibleDownloads.map(download => (
        <div
          key={download.taskId}
          className="mb-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-5 duration-300"
        >
          <div className="p-4">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-white truncate">
                    Generating Video
                  </h4>
                  <p className="text-xs text-gray-300 truncate">
                    {download.title}
                  </p>
                </div>
              </div>

              {/* Dismiss button */}
              <button
                onClick={() => handleDismiss(download.taskId)}
                className="flex-shrink-0 ml-2 p-1 text-gray-400 hover:text-white transition-colors"
                aria-label="Dismiss"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Progress indicator */}
            {download.status === 'generating' && (
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full animate-pulse w-full"></div>
                  </div>
                </div>
                <p className="text-xs text-gray-300">
                  This may take 1-6 minutes...
                </p>
              </div>
            )}

            {/* Status message */}
            {download.status === 'failed' && (
              <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-2 mb-2">
                <p className="text-xs text-red-200">
                  {download.error || 'Generation failed'}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2">
              {download.status === 'generating' && (
                <button
                  onClick={() => handleCancel(download.taskId)}
                  className="flex-1 text-xs px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
              )}
              <p className="text-xs text-gray-400">
                You can navigate away - we'll download when ready
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default VideoDownloadNotification;
